// client/src/components/pages/EmailReports/index.tsx (converted from .js, ST-00229)
//
// The Email Reports page: the report list with its status / schedule badges, and the Quick and
// Custom report modals. RPT-11 (ST-00203): a saved report replaces its list row straight away
// with the schedule the modal saved merged in (a new scheduled Quick report used to show
// "Manual" until a reload, and switching a schedule off kept "Scheduled"), then the list is
// refreshed in the background. The selected report id (preview, history) and the report being
// edited are now two separate pieces of state (one variable used to hold either).
//
// ST-00238: each row has an on/off switch (ReportActiveSwitch) beside its status. One click
// flips the row at once and calls PATCH /email-reports/:id/active; a failed call flips it back
// and says why. No confirmation. Only administrators (the roles that can edit a report) can
// press it; everyone else sees the state. A switched-off row is dimmed, its schedule shows
// "Paused" instead of the next run, and Send Now / Preview are unavailable until it is on.
import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import type { IconType } from 'react-icons';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaPaperPlane,
  FaEye,
  FaHistory,
  FaTimes,
  FaEnvelope,
  FaRobot,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaSortAmountDown,
  FaSortAmountUp,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import ThemedSelect from '../../common/ThemedSelect';
import ReportConfigModal from './ReportConfigModal';
import EmailPreviewModal from './EmailPreviewModal';
import ReportTypeChooser from './ReportTypeChooser';
import AdvancedReportConfigModal from './AdvancedReportConfigModal';
import ReportActiveSwitch from './ReportActiveSwitch';
import AiAssistModal from '../../shared/AiAssistPanel';
import LexiBridgeLoader from '../../common/LexiBridgeLoader';
import { PageHeader } from '../../../types/commonComponents';
import type {
  EmailReportActiveResponse,
  EmailReportApiError,
  EmailReportDetail,
  EmailReportExecution,
  EmailReportExecutionPage,
  EmailReportListRow,
} from '../../../types/clientPages/emailReports';

type ReportStatus = 'inactive' | 'scheduled' | 'manual';
type SortField = 'id' | 'name' | 'type' | 'status' | 'recipients' | 'lastRun' | 'created';
type ActionVariant = 'default' | 'send' | 'preview' | 'history' | 'edit' | 'delete';

/** What the AI panel (shared/AiAssistPanel) needs to edit a Custom report. */
interface AiEditReport {
  reportId: number;
  reportName: string;
  description?: string | null;
  queryId?: number | null;
  queryName: string;
  querySql: string;
  templateId?: number | null;
  templateName: string;
  templateHtml: string;
  subjectTemplate: string;
}

/** GET /email-reports/templates/:id (either naming). */
interface EmailTemplateBody {
  body_template?: string;
  bodyTemplate?: string;
  template_name?: string;
  templateName?: string;
  subject_template?: string;
  subjectTemplate?: string;
}

/** The roles the server lets change a report (routes/emailReports requireEmailAdmin). */
const EMAIL_ADMIN_ROLES = ['administrator', 'global-admin'] as const;

const errorText = (err: unknown): string => {
  const e = err as EmailReportApiError;
  return e.response?.data?.message || e.message || 'Unknown error';
};

/** The switch route answers `{ error }` (400 / 404 / 500) or `{ message }` (403). */
const switchErrorText = (err: unknown): string => {
  const e = err as EmailReportApiError;
  return e.response?.data?.error || e.response?.data?.message || e.message || 'Unknown error';
};

/** The saved row replaces the report's row, or is added for a new report. */
const upsertReport = (
  reports: EmailReportListRow[],
  saved: EmailReportListRow
): EmailReportListRow[] =>
  reports.some((r) => r.id === saved.id)
    ? reports.map((r) => (r.id === saved.id ? { ...r, ...saved } : r))
    : [...reports, saved];

const StatusBadge = ({ report }: { report: EmailReportListRow }) => {
  if (!report.is_active) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-slate-50 text-slate-500 border-slate-200">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        Inactive
      </span>
    );
  }
  if (report.schedule_active) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulseSync" />
        Scheduled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      Manual
    </span>
  );
};

const TypeBadge = ({ type }: { type?: string | null }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${
      type === 'advanced'
        ? 'bg-violet-50 text-violet-700 border-violet-200'
        : 'bg-primary-50 text-primary-700 border-primary-200'
    }`}
  >
    {type === 'advanced' ? 'Custom' : 'Quick'}
  </span>
);

interface ActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  icon: IconType;
  variant?: ActionVariant;
}

const ActionButton = ({
  onClick,
  disabled,
  title,
  icon: Icon,
  variant = 'default',
}: ActionButtonProps) => {
  const variants: Record<ActionVariant, string> = {
    default: 'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
    send: 'text-primary-500 hover:text-primary-700 hover:bg-primary-50',
    preview: 'text-primary-500 hover:text-primary-700 hover:bg-primary-50',
    history: 'text-violet-500 hover:text-violet-700 hover:bg-violet-50',
    edit: 'text-amber-500 hover:text-amber-700 hover:bg-amber-50',
    delete: 'text-red-400 hover:text-red-600 hover:bg-red-50',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded-lg transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${variants[variant]}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
};

interface ExecutionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: number | null;
}

const ExecutionHistoryModal = ({ isOpen, onClose, reportId }: ExecutionHistoryModalProps) => {
  const [executions, setExecutions] = useState<EmailReportExecution[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  const loadExecutions = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const res = await api.emailReports.getExecutions(reportId, page, 10);
      const body = res.data as EmailReportExecutionPage;
      setExecutions(body.executions || []);
      setPagination(body.pagination || { total: 0, pages: 1 });
    } catch (err) {
      console.error('Error loading executions:', err);
    } finally {
      setLoading(false);
    }
  }, [reportId, page]);

  useEffect(() => {
    if (isOpen && reportId) {
      setPage(1);
      loadExecutions();
    }
  }, [isOpen, reportId, loadExecutions]);

  useEffect(() => {
    if (isOpen && reportId) {
      loadExecutions();
    }
  }, [page, isOpen, reportId, loadExecutions]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 dark:bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-xl border border-slate-200 w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
              <FaHistory className="text-violet-600 text-sm" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 tracking-tight">
                Execution History
              </h2>
              <p className="text-xs text-slate-500">
                {pagination.total} total execution{pagination.total !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin text-primary-500 text-xl mr-3" />
              <span className="text-sm text-slate-500">Loading history...</span>
            </div>
          ) : executions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                <FaHistory className="text-slate-400 text-lg" />
              </div>
              <p className="text-sm font-medium text-slate-700">No executions yet</p>
              <p className="text-xs text-slate-400 mt-1">Reports will appear here once sent</p>
            </div>
          ) : (
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                    Delivered
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody>
                {executions.map((exec, i) => (
                  <tr
                    key={exec.id}
                    className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors"
                    style={{ animationDelay: `${i * 30}ms` }}
                  >
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-700 font-mono tabular-nums">
                      {new Date(exec.execution_time).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${
                          exec.status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-red-50 text-red-700 border-red-200'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${exec.status === 'completed' ? 'bg-emerald-500' : 'bg-red-500'}`}
                        />
                        {exec.status === 'completed' ? 'Sent' : 'Failed'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 font-mono tabular-nums">
                      {exec.emails_sent || 0}
                      <span className="text-slate-300 mx-0.5">/</span>
                      {exec.recipient_count || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500 font-mono tabular-nums">
                      {exec.duration_ms ? `${(exec.duration_ms / 1000).toFixed(1)}s` : '—'}
                    </td>
                    <td
                      className="px-4 py-3 text-xs text-red-600 max-w-[200px] truncate"
                      title={exec.error_message || ''}
                    >
                      {exec.error_message || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50/50">
            <span className="text-xs text-slate-500 tabular-nums font-mono">
              Page {page} of {pagination.pages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-surface hover:text-slate-700 disabled:opacity-40 transition-colors"
              >
                <FaChevronLeft className="w-3 h-3" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-surface hover:text-slate-700 disabled:opacity-40 transition-colors"
              >
                <FaChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EmailReports = () => {
  const { hasPermission, hasAnyAppRole } = useAuth();
  const showAiAssist = hasPermission && hasPermission('ai.assist');
  // Switching a report on / off needs what editing needs (ST-00238).
  const canManageReports = hasAnyAppRole(EMAIL_ADMIN_ROLES);
  // Reports whose on/off call is in flight.
  const [switching, setSwitching] = useState<ReadonlySet<number>>(() => new Set());
  const [reports, setReports] = useState<EmailReportListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The report whose preview / history is open, and the report being edited (was one variable).
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [editReport, setEditReport] = useState<EmailReportDetail | null>(null);
  const [showTypeChooser, setShowTypeChooser] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateAdvancedModal, setShowCreateAdvancedModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditAdvancedModal, setShowEditAdvancedModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiEditReport, setAiEditReport] = useState<AiEditReport | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const getReportStatus = useCallback((report: EmailReportListRow): ReportStatus => {
    if (!report.is_active) return 'inactive';
    if (report.schedule_active) return 'scheduled';
    return 'manual';
  }, []);

  const filteredReports = useMemo(() => {
    let result = [...reports];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.name?.toLowerCase().includes(q) ||
          r.description?.toLowerCase().includes(q) ||
          r.query_name?.toLowerCase().includes(q)
      );
    }

    if (filterType !== 'all') {
      result = result.filter((r) => (r.report_type || 'basic') === filterType);
    }

    if (filterStatus !== 'all') {
      result = result.filter((r) => getReportStatus(r) === filterStatus);
    }

    result.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'id':
          cmp = (a.id || 0) - (b.id || 0);
          break;
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'type':
          cmp = (a.report_type || 'basic').localeCompare(b.report_type || 'basic');
          break;
        case 'status':
          cmp = getReportStatus(a).localeCompare(getReportStatus(b));
          break;
        case 'recipients':
          cmp = (a.recipients?.length || 0) - (b.recipients?.length || 0);
          break;
        case 'lastRun':
          cmp = new Date(a.last_run_time || 0).getTime() - new Date(b.last_run_time || 0).getTime();
          break;
        case 'created':
          cmp = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        default:
          break;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [reports, searchQuery, filterType, filterStatus, sortField, sortOrder, getReportStatus]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortHeader = ({
    field,
    children,
    className = '',
  }: {
    field: SortField;
    children: ReactNode;
    className?: string;
  }) => (
    <th
      className={`px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest cursor-pointer select-none hover:text-slate-700 transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortField === field &&
          (sortOrder === 'asc' ? (
            <FaSortAmountUp className="w-2.5 h-2.5 text-primary-500" />
          ) : (
            <FaSortAmountDown className="w-2.5 h-2.5 text-primary-500" />
          ))}
      </span>
    </th>
  );

  useEffect(() => {
    loadReports();
  }, []);

  // `silent`: a background refresh after a save (no overlay; a failure keeps the list).
  const loadReports = async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.emailReports.getAll();
      setReports(response.data as EmailReportListRow[]);
      setError(null);
    } catch (err) {
      console.error('Error loading email reports:', err);
      if (!silent) setError('Failed to load email reports');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // A report was saved: show its row (with the saved schedule) at once, then refresh the list
  // for the columns only the list query has (last run, grouping count).
  const handleReportSaved = (saved: EmailReportListRow) => {
    setReports((prev) => upsertReport(prev, saved));
    void loadReports({ silent: true });
  };

  // ST-00238: one click switches a report on or off. The row flips at once (optimistic); the
  // server's answer then sets the flag and the schedule's next run (switching on re-arms it).
  // A failed call puts the row back as it was and says why.
  const handleToggleActive = async (report: EmailReportListRow, active: boolean) => {
    const { id, name } = report;
    const previous = report.is_active;
    setSwitching((prev) => new Set(prev).add(id));
    setReports((prev) => prev.map((r) => (r.id === id ? { ...r, is_active: active } : r)));
    try {
      const res = await api.emailReports.setActive(id, active);
      const saved = res.data as EmailReportActiveResponse;
      setReports((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                is_active: saved.is_active,
                updated_at: saved.updated_at ?? r.updated_at,
                schedule_active:
                  saved.schedule_active !== undefined ? saved.schedule_active : r.schedule_active,
                next_run_time:
                  saved.next_run_time !== undefined ? saved.next_run_time : r.next_run_time,
              }
            : r
        )
      );
      toast.success(
        saved.is_active
          ? `"${name}" is switched on`
          : `"${name}" is switched off. It will not be sent.`
      );
    } catch (err) {
      console.error('Error switching email report on/off:', err);
      setReports((prev) => prev.map((r) => (r.id === id ? { ...r, is_active: previous } : r)));
      toast.error(`Could not switch "${name}" ${active ? 'on' : 'off'}: ${switchErrorText(err)}`);
    } finally {
      setSwitching((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleSendReport = async (reportId: number) => {
    try {
      setLoading(true);
      await api.emailReports.send(reportId);
      toast.success('Email report sent successfully!');
      await loadReports();
    } catch (err) {
      console.error('Error sending email report:', err);
      toast.error('Failed to send email report: ' + errorText(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: number) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this email report? This action cannot be undone.'
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      await api.emailReports.delete(reportId);
      setReports(reports.filter((r) => r.id !== reportId));
      toast.success('Email report deleted successfully!');
    } catch (err) {
      console.error('Error deleting email report:', err);
      toast.error('Failed to delete email report: ' + errorText(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewReport = (reportId: number) => {
    setSelectedReportId(reportId);
    setShowPreviewModal(true);
  };

  const handleViewHistory = (reportId: number) => {
    setSelectedReportId(reportId);
    setShowHistoryModal(true);
  };

  const handleEditReport = async (report: EmailReportListRow) => {
    setLoading(true);
    try {
      const response = await api.emailReports.get(report.id);
      setEditReport(response.data as EmailReportDetail);
      if (report.report_type === 'advanced') {
        setShowEditAdvancedModal(true);
      } else {
        setShowEditModal(true);
      }
    } catch (err) {
      const e = err as EmailReportApiError;
      toast.error(
        'Failed to load report details: ' +
          (e.response?.data?.details ||
            e.response?.data?.error ||
            e.response?.data?.message ||
            e.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAiEditReport = async (report: EmailReportListRow) => {
    if (report.report_type !== 'advanced' || !report.query_id) {
      toast.error('AI editing is only available for Custom (advanced) reports with a linked query');
      return;
    }
    setLoading(true);
    try {
      const reportRes = await api.emailReports.get(report.id);
      const reportData = reportRes.data as EmailReportDetail;

      let templateHtml = '';
      let templateName = '';
      let subjectTemplate = '';
      if (reportData.email_template_id) {
        try {
          // (The .js tested `await api.emailReports.getTemplates` — a function, always truthy.)
          const tRes = await api.get(`/email-reports/templates/${reportData.email_template_id}`);
          const t = tRes?.data as EmailTemplateBody | undefined;
          if (t) {
            templateHtml = t.body_template || t.bodyTemplate || '';
            templateName = t.template_name || t.templateName || '';
            subjectTemplate = t.subject_template || t.subjectTemplate || '';
          }
        } catch {
          // Template fetch failed — continue with empty template
        }
      }

      setAiEditReport({
        reportId: report.id,
        reportName: reportData.name,
        description: reportData.description,
        queryId: reportData.query_id,
        queryName: reportData.query_name || report.query_name || '',
        querySql: reportData.sql_query || '',
        templateId: reportData.email_template_id,
        templateName,
        templateHtml,
        subjectTemplate,
      });
      setShowAiModal(true);
    } catch (err) {
      const e = err as EmailReportApiError;
      toast.error(
        'Failed to load report for AI editing: ' + (e.response?.data?.error || e.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const formatSchedule = (
    report: EmailReportListRow
  ): { label: string; detail: string | null } | null => {
    if (!report.schedule_active) return null;

    const type = report.schedule_type || '';
    const time = report.schedule_time;

    if (type === 'cron' && report.cron_expression) {
      return { label: 'Cron', detail: report.cron_expression };
    }
    if (type === 'recurring' && report.interval_minutes) {
      return { label: `Every ${report.interval_minutes}m`, detail: null };
    }
    if (type === 'once') {
      return { label: 'One-time', detail: null };
    }

    // schedule_time arrives as "HH:mm:ss" (pg TIME) or a full ISO datetime from
    // legacy rows — either way only the time of day is meaningful (ST-00099).
    const timeOfDay = time && (String(time).match(/(\d{2}):(\d{2})/) || [])[0];
    return {
      label: `${type.charAt(0).toUpperCase() + type.slice(1)}`,
      detail: timeOfDay ? `at ${timeOfDay}` : null,
    };
  };

  const formatNextRun = (report: EmailReportListRow): string | null => {
    if (!report.next_run_time) return null;
    const d = new Date(report.next_run_time);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    if (diffMs < 0) return 'Overdue';
    if (diffMs < 3600000) return `in ${Math.round(diffMs / 60000)}m`;
    if (diffMs < 86400000) return `in ${Math.round(diffMs / 3600000)}h`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  if (loading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-slate-50">
        <LexiBridgeLoader message="Loading reports" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaEnvelope}
        title="Email Reports"
        subtitle="Automated email reports for daily operations"
        actions={
          <>
            {showAiAssist && (
              <button
                type="button"
                onClick={() => {
                  setAiEditReport(null);
                  setShowAiModal(true);
                }}
                aria-label="Lexi AI"
                title="Lexi AI"
                className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 rounded-lg transition-all shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                <FaRobot className="w-3.5 h-3.5" aria-hidden="true" />
                <span className="page-header__label">Lexi AI</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowTypeChooser(true)}
              aria-label="Create Report"
              title="Create Report"
              className="inline-flex items-center justify-center gap-2 h-10 px-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <FaPlus className="w-3.5 h-3.5" aria-hidden="true" />
              <span>
                Create<span className="page-header__label"> Report</span>
              </span>
            </button>
          </>
        }
      />

      <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-5">
        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 animate-fadeIn">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Filter Bar */}
        {reports.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-3 animate-fadeIn"
            style={{ animationDelay: '40ms' }}
          >
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm bg-surface border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 placeholder-slate-400 transition-colors"
              />
            </div>
            <ThemedSelect
              value={filterType}
              onChange={(e) => setFilterType(String(e.target.value))}
              className="h-9 text-sm"
            >
              <option value="all">All Types</option>
              <option value="basic">Quick</option>
              <option value="advanced">Custom</option>
            </ThemedSelect>
            <ThemedSelect
              value={filterStatus}
              onChange={(e) => setFilterStatus(String(e.target.value))}
              className="h-9 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="manual">Manual</option>
              <option value="inactive">Inactive</option>
            </ThemedSelect>
          </div>
        )}

        {/* Reports Card */}
        <div
          className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-fadeIn"
          style={{ animationDelay: '60ms' }}
        >
          {reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative mb-4">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
                  <FaEnvelope className="text-slate-400 text-2xl" />
                </div>
                <div className="absolute inset-0 rounded-2xl animate-ripple border-2 border-slate-200" />
              </div>
              <h3 className="text-base font-bold text-slate-800">No email reports</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5 max-w-sm text-center">
                Create your first automated email report to get started
              </p>
              <button
                onClick={() => setShowTypeChooser(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors shadow-sm"
              >
                <FaPlus className="text-xs" />
                <span>Create Report</span>
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <SortHeader field="id" className="pl-5 pr-2 w-10">
                      #
                    </SortHeader>
                    <SortHeader field="name" className="pl-2 pr-4">
                      Report
                    </SortHeader>
                    <SortHeader field="type">Type</SortHeader>
                    <SortHeader field="status">Status</SortHeader>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                      Schedule
                    </th>
                    <SortHeader field="recipients">Recipients</SortHeader>
                    <SortHeader field="lastRun">Last Run</SortHeader>
                    <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-5 py-10 text-center text-sm text-slate-400">
                        No reports match your filters
                      </td>
                    </tr>
                  ) : (
                    filteredReports.map((report, i) => {
                      const schedule = formatSchedule(report);
                      const nextRun = formatNextRun(report);
                      // A switched-off report reads as off at a glance (ST-00238).
                      const inactive = !report.is_active;
                      const dim = inactive ? 'opacity-60' : '';
                      return (
                        <tr
                          key={report.id}
                          data-inactive={inactive || undefined}
                          className={`border-b border-slate-50 last:border-b-0 hover:bg-slate-50/70 transition-colors group ${
                            inactive ? 'bg-slate-50/60' : ''
                          }`}
                          style={{ animationDelay: `${i * 40}ms` }}
                        >
                          {/* Report Number */}
                          <td className={`pl-5 pr-2 py-3.5 ${dim}`}>
                            <span className="text-xs font-mono tabular-nums text-slate-400">
                              {report.id}
                            </span>
                          </td>

                          {/* Report Name */}
                          <td className={`pl-2 pr-4 py-3.5 ${dim}`}>
                            <div className="min-w-[180px]">
                              <p
                                className={`text-sm font-semibold tracking-tight leading-tight ${
                                  inactive ? 'text-slate-500' : 'text-slate-900'
                                }`}
                              >
                                {report.name}
                              </p>
                              {report.description && (
                                <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[260px]">
                                  {report.description}
                                </p>
                              )}
                              {report.report_type === 'advanced' && report.query_name && (
                                <p className="text-[10px] text-slate-400 mt-0.5 font-mono truncate max-w-[220px]">
                                  {report.query_name}
                                </p>
                              )}
                              {report.report_type !== 'advanced' &&
                                (report.grouping_count ?? 0) > 0 && (
                                  <p className="text-[10px] text-slate-400 mt-0.5">
                                    {report.grouping_count} route
                                    {report.grouping_count !== 1 ? 's' : ''}
                                  </p>
                                )}
                            </div>
                          </td>

                          {/* Type */}
                          <td className={`px-4 py-3.5 ${dim}`}>
                            <TypeBadge type={report.report_type} />
                          </td>

                          {/* Status: the on/off switch (ST-00238) and the badge */}
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-2.5">
                              <ReportActiveSwitch
                                reportName={report.name}
                                checked={!!report.is_active}
                                readOnly={!canManageReports}
                                busy={switching.has(report.id)}
                                onChange={(on) => void handleToggleActive(report, on)}
                              />
                              <StatusBadge report={report} />
                            </div>
                          </td>

                          {/* Schedule */}
                          <td className={`px-4 py-3.5 ${dim}`}>
                            {schedule ? (
                              <div>
                                <p className="text-sm text-slate-700 font-medium">
                                  {schedule.label}
                                </p>
                                {schedule.detail && (
                                  <p className="text-xs text-slate-400">{schedule.detail}</p>
                                )}
                                {inactive ? (
                                  <p className="text-[10px] text-slate-500 font-semibold mt-0.5">
                                    Paused
                                  </p>
                                ) : (
                                  nextRun && (
                                    <p className="text-[10px] text-primary-600 font-semibold mt-0.5">
                                      Next: {nextRun}
                                    </p>
                                  )
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>

                          {/* Recipients */}
                          <td className={`px-4 py-3.5 ${dim}`}>
                            <span className="text-sm text-slate-700 font-mono tabular-nums">
                              {report.recipients?.length || 0}
                            </span>
                          </td>

                          {/* Last Run */}
                          <td className={`px-4 py-3.5 ${dim}`}>
                            {report.last_run_time ? (
                              <div>
                                <p className="text-xs text-slate-600 font-mono tabular-nums">
                                  {new Date(report.last_run_time).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </p>
                                <p className="text-[10px] text-slate-400 font-mono tabular-nums">
                                  {new Date(report.last_run_time).toLocaleTimeString(undefined, {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                                {report.last_run_status && (
                                  <span
                                    className={`inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold ${
                                      report.last_run_status === 'completed'
                                        ? 'text-emerald-600'
                                        : 'text-red-500'
                                    }`}
                                  >
                                    <span
                                      className={`w-1 h-1 rounded-full ${
                                        report.last_run_status === 'completed'
                                          ? 'bg-emerald-500'
                                          : 'bg-red-500'
                                      }`}
                                    />
                                    {report.last_run_status === 'completed' ? 'Sent' : 'Failed'}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">Never</span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3.5">
                            <div className="flex items-center justify-end gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                              <ActionButton
                                onClick={() => handleSendReport(report.id)}
                                disabled={loading || inactive}
                                title={
                                  inactive ? 'Send Now (switch the report on first)' : 'Send Now'
                                }
                                icon={FaPaperPlane}
                                variant="send"
                              />
                              <ActionButton
                                onClick={() => handlePreviewReport(report.id)}
                                disabled={inactive}
                                title={
                                  inactive ? 'Preview (switch the report on first)' : 'Preview'
                                }
                                icon={FaEye}
                                variant="preview"
                              />
                              <ActionButton
                                onClick={() => handleViewHistory(report.id)}
                                title="History"
                                icon={FaHistory}
                                variant="history"
                              />
                              {report.report_type === 'advanced' && showAiAssist && (
                                <ActionButton
                                  onClick={() => handleAiEditReport(report)}
                                  disabled={loading}
                                  title="AI Edit"
                                  icon={FaRobot}
                                  variant="preview"
                                />
                              )}
                              <ActionButton
                                onClick={() => handleEditReport(report)}
                                title="Edit"
                                icon={FaEdit}
                                variant="edit"
                              />
                              <ActionButton
                                onClick={() => handleDeleteReport(report.id)}
                                disabled={loading}
                                title="Delete"
                                icon={FaTrash}
                                variant="delete"
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary bar */}
        {reports.length > 0 && (
          <div
            className="flex items-center justify-between px-1 animate-fadeIn"
            style={{ animationDelay: '120ms' }}
          >
            <p className="text-xs text-slate-400">
              {filteredReports.length !== reports.length
                ? `Showing ${filteredReports.length} of ${reports.length} reports`
                : `${reports.length} report${reports.length !== 1 ? 's' : ''}`}{' '}
              &middot; {reports.filter((r) => getReportStatus(r) === 'scheduled').length} scheduled
              &middot; {reports.filter((r) => r.is_active).length} active
            </p>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && reports.length > 0 && (
          <div className="fixed inset-0 bg-slate-900/30 dark:bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-surface px-6 py-5 rounded-xl shadow-xl border border-slate-200 flex items-center gap-3">
              <FaSpinner className="animate-spin text-primary-500" />
              <span className="text-sm font-medium text-slate-700">Processing...</span>
            </div>
          </div>
        )}

        {/* Modals */}
        <ReportTypeChooser
          isOpen={showTypeChooser}
          onClose={() => setShowTypeChooser(false)}
          onSelectBasic={() => {
            setShowTypeChooser(false);
            setShowCreateModal(true);
          }}
          onSelectAdvanced={() => {
            setShowTypeChooser(false);
            setShowCreateAdvancedModal(true);
          }}
        />

        <ReportConfigModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={(newReport) => {
            handleReportSaved(newReport);
            setShowCreateModal(false);
          }}
        />

        <ReportConfigModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditReport(null);
          }}
          report={editReport}
          onSave={(updatedReport) => {
            handleReportSaved(updatedReport);
            setShowEditModal(false);
            setEditReport(null);
          }}
        />

        <AdvancedReportConfigModal
          isOpen={showCreateAdvancedModal}
          onClose={() => setShowCreateAdvancedModal(false)}
          onSave={(newReport) => {
            handleReportSaved(newReport);
            setShowCreateAdvancedModal(false);
          }}
        />

        <AdvancedReportConfigModal
          isOpen={showEditAdvancedModal}
          onClose={() => {
            setShowEditAdvancedModal(false);
            setEditReport(null);
          }}
          report={editReport}
          onSave={(updatedReport) => {
            handleReportSaved(updatedReport);
            setShowEditAdvancedModal(false);
            setEditReport(null);
          }}
        />

        <EmailPreviewModal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          reportId={selectedReportId}
        />

        <ExecutionHistoryModal
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedReportId(null);
          }}
          reportId={selectedReportId}
        />

        {showAiAssist && (
          <AiAssistModal
            isOpen={showAiModal}
            onClose={() => {
              setShowAiModal(false);
              setAiEditReport(null);
            }}
            outputType="email"
            onCreated={() => loadReports()}
            editReport={aiEditReport}
          />
        )}
      </div>
    </div>
  );
};

export default EmailReports;
